import { FORM_VALIDATOR_TYPES } from '../constants';
import { IAgreementFormData, IAgreementTemplate, IAgreementContent } from '../interface/agreement';

// ============================================================================
// TRIAL PERIOD CONFIGURATION
// ============================================================================
// CRITICAL: This constant controls the trial period duration displayed in the
// agreement template. Update this single value to change all references.
export const TRIAL_PERIOD_DURATION = 'fourteen (14) days';
export const TRIAL_PERIOD_DURATION_SHORT = '14 days';

// Initial form state (matches Guardian form pattern)
export const AGREEMENT_FORM_INIT: IAgreementFormData = {
  // Date components
  startDay: {
    value: new Date().getDate().toString(),
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  startMonth: {
    value: new Date().toLocaleString('default', { month: 'long' }),
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  startYear: {
    value: new Date().getFullYear().toString(),
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  // Organization details
  organizationName: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  organizationAddress: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  businessRegistrationNumber: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  // Signature fields
  primaryContactName: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  primaryContactTitle: {
    value: '',
    isRequired: false,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  primaryContactSignature: {
    value: '', // empty until user draws
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.TEXT,
    disable: false,
  },
  agreementDate: {
    value: new Date().toISOString().split('T')[0], // Today
    isRequired: false,
    error: null,
    validator: null,
    disable: true, // Read-only
  },
  acknowledgmentAccepted: {
    value: false,
    isRequired: true,
    error: null,
    validator: FORM_VALIDATOR_TYPES.BOOLEAN,
    disable: false,
  },
};

// Mock agreement template data
export const AGREEMENT_TEMPLATE_MOCK: IAgreementTemplate = {
  startDate: new Date().toISOString().split('T')[0],
  companyName: 'Sanlabz (Private) Limited',
  companyRegNo: 'PV00295232',
  termsOfServiceUrl: 'https://www.classveew.com/terms',
  privacyPolicyUrl: 'https://www.classveew.com/privacy',
};

// Agreement content
export const AGREEMENT_CONTENT: IAgreementContent = {
  title: 'CLASSVEEW ACCESS CONFIRMATION AND TRIAL AGREEMENT',
  section1: `1. Initial Access and Platform Use
The Customer is granted access to the Classveew Software to evaluate its suitability for internal use.

1.1. Access Granted: The Company grants the Customer a non-exclusive, revocable right to access the complete Classveew platform features for a trial period of ${TRIAL_PERIOD_DURATION} days from the effective date, or another period mutually agreed upon in writing.

1.2. Trial End: This access will automatically end upon the expiration of the above trial period.

1.3. Service Closure: The Company retains the right to end this trial access at any time if necessary and will provide the Customer with at least one (1) week's prior notice in such cases.`,
  section2: `2. Legal Acknowledgment and Binding Terms
The Customer understands that by signing this Confirmation, they are agreeing to the following essential legal terms that govern their use of the platform.

2.1. Full Terms: The Customer acknowledges and agrees that the full Classveew Terms of Service and Conditions of Use and Privacy Policy (available at www.classveew.com) are incorporated by reference and immediately apply to their use.

2.2. Essential Protections Agreed To: By signing below, the Customer specifically agrees to the following protective principles:
  • Software Ownership: The Company retains all rights, ownership, and Intellectual Property related to the Software, code, and platform.
  • Customer Data Responsibility: The Customer is responsible for the accuracy, security, and legality of all data uploaded to the platform, including securing necessary legal consents from parents and staff (PDPA compliance).
  • Limited Company Liability: The Customer agrees that the Company's financial responsibility for any service issues or damages is capped and limited to the lowest amount permitted by law.
  • Payment Gateway: The platform facilitates payments via the WebX Pay gateway. The Customer is responsible for its own WebX Pay account, all associated transaction fees, chargebacks, and financial compliance.
  • Governing Law: This agreement and all related use of the platform are governed by the laws and courts of the Democratic Socialist Republic of Sri Lanka.`,
  section3: `3. Continuation of Service
3.1. Subscribing to Continue: If the Customer wishes to continue using the Classveew platform after the trial period, the Customer will need to subscribe to a paid plan.

3.2. Next Step: To purchase a subscription (Basic Plan, Standard, Premium, or Corporate), the Customer must execute a separate CLASSVEEW SUBSCRIPTION AGREEMENT. Access will cease promptly upon the expiration of the ${TRIAL_PERIOD_DURATION_SHORT} trial period if no Subscription Agreement is in place.

Customer Confirmation: The Customer confirms it has read and agrees to the terms of this Access Confirmation and the full Terms of Service.`,
  acknowledgmentText:
    'I have read and agree to this Classveew Access Confirmation and all linked Terms of Service',
};

// Validation messages
export const AGREEMENT_VALIDATION_MESSAGES = {
  organizationName: 'Organization name must be between 3 and 255 characters',
  organizationAddress: 'Address must be between 5 and 500 characters',
  businessRegistrationNumber: 'Business registration number must be between 3 and 50 characters',
  primaryContactName: 'Name must be between 2 and 100 characters',
  primaryContactTitle: 'Title must be between 2 and 100 characters',
  acknowledgmentAccepted: 'You must accept the agreement to proceed',
  scrollRequired: 'Please scroll to read the full agreement before accepting',
};

// Field validation rules
export const AGREEMENT_FIELD_RULES = {
  organizationName: { min: 3, max: 255 },
  organizationAddress: { min: 5, max: 500 },
  businessRegistrationNumber: { min: 3, max: 50 },
  primaryContactName: { min: 2, max: 100 },
  primaryContactTitle: { min: 2, max: 100 },
};

// ============================================================================
// SUBSCRIPTION AGREEMENT CONTENT
// ============================================================================
// CRITICAL: Master Subscription Agreement for paid subscription plans
// Different from TRIAL AGREEMENT above - this is for active subscription terms
export const SUBSCRIPTION_AGREEMENT_CONTENT: IAgreementContent = {
  title: 'CLASSVEEW – MASTER SUBSCRIPTION AGREEMENT',
  section1: `1. ACCESS RIGHTS & LICENSE
1.1 License Grant
The Company grants the Customer a non-exclusive, non-transferable, revocable license to use Classveew web and mobile platforms for internal academic and administrative operations.

1.2 Authorized Users
Depending on the subscription tier, access may include:
• Staff
• Teachers
• Students
• Admin users
• Parents/Guardian (direct or via parent-paid plans)

1.3 Subscription Plans
Plans include but are not limited to: Basic, Standard, Premium, and Corporate. Features available under each subscription plan are those published by the Company at the time of use, either on the Company's official website or within the Software. The Company may introduce new features or enhancements from time to time and may reasonably modify existing features, provided that such changes do not materially reduce the core functionality of the Customer's active subscription during a paid term.`,

  section2: `2. FEES, BILLING & PAYMENT
2.1 Subscription Fees
Fees are based on:
• Per-student annual pricing
• Feature tier
• Additional purchased modules
• Any other agreed criteria

2.2 Payment Terms
Unless stated otherwise:
• Invoices are due within seven (7) days
• Late payment may result in suspension

2.3 Taxes
All fees are exclusive of taxes. Customer is responsible for applicable taxes.

2.4 Company Subscription Payments
Subscription fees payable by the Customer to the Company for use of the Software shall be paid directly to the Company through methods communicated by the Company, which may include bank transfer or authorized third-party payment processors. The Customer is responsible for all applicable transaction fees related to such payments.`,

  section3: `3. SERVICE LEVEL, SUPPORT & AVAILABILITY
3.1 Support
Support level as per the Classveew Support Policy.

3.2 Software Uptime
We use commercially reasonable efforts to maintain service continuity. The Software is provided AS IS and AS AVAILABLE with no guaranteed uptime SLA.

4. INTELLECTUAL PROPERTY
4.1 Ownership
The Company retains all global intellectual property rights to Classveew:
• Software
• Source code
• UI/UX designs
• Databases
• Features
• Documentation

4.2 Restrictions
Customer may not: Copy, Reverse engineer, Modify, Resell, Frame, or Redistribute unless permitted in writing.

5. CUSTOMER RESPONSIBILITIES
5.1 Data Legality
The Customer is fully responsible for the lawful collection and upload of all personal data.

5.2 Consent Obligations
Customer confirms it has obtained required consents for: Student data, Parent/guardian data, Staff data, Photos, videos, and educational records, and any other data and information. This includes compliance with any privacy laws applicable in the Customer's country (GDPR, COPPA, PIPEDA, PDPA, or other local frameworks).

5.3 Payment Gateways
When using integrated third-party gateways (Stripe, WebX Pay, etc.), the Customer acknowledges that the Software provides technical integration. The Customer is solely responsible for: (a) establishing and maintaining merchant account, (b) all transaction fees and chargebacks, and (c) complying with Payment Gateway provider terms.

5.4 Media Consent Responsibility
The Customer is solely responsible for ensuring that any images, videos, or media of students uploaded to the Platform are lawfully obtained and used with valid parental or guardian consent. The Company does not verify such consent and shall not be liable for unauthorized or improper use.

5.5 Force Majeure
Neither party shall be liable for delays or failures caused by events beyond reasonable control, including natural disasters, internet outages, or government actions.

6. DATA PRIVACY & SECURITY
6.1 Data Controller & Processor Roles
• Customer = Data Controller
• Company = Data Processor

6.2 Security
The Company uses reasonable administrative, physical, and technical safeguards designed to protect the security, confidentiality, and integrity of the Platform and Customer Data. The Platform is hosted on Amazon Web Services (AWS) infrastructure.

6.3 Data Transfers
Customer acknowledges that data may be stored or processed in global cloud regions.

6.4 Data Export & Deletion
Upon termination: Customer has 30 days to export data. After 30 days, data may be deleted.

6.5 Use of Sub-Processors
The Customer acknowledges that the Company may engage sub-processors, including cloud infrastructure providers, analytics services, and communication providers. The Company shall remain responsible for compliance obligations but shall not be liable for failures beyond reasonable control.

7. LIMITATIONS OF LIABILITY
7.1 No Warranty
The Software is provided without warranties of any kind.

7.2 Liability Cap
Total liability is limited to the greater of: USD $100 or Total fees paid in the previous 12 months.

7.3 Exclusion of Damages
No liability for: Indirect damages, Lost profits, Loss of data, or Business interruption.

7.4 Disclaimer of Results
The Company does not guarantee academic results, student performance, compliance outcomes, or regulatory approvals arising from the use of the Software.

8. TERM & TERMINATION
8.1 Term
This Agreement continues until terminated.

8.2 Termination with Notice
Either party may terminate with 30 days' prior written notice.

8.3 Immediate Termination
Company may terminate immediately for: Non-payment, Misuse, or Material breach.

8.4 Post-Termination
Access stops immediately; data export rules apply.

8.5 Suspension of Access
The Company may temporarily suspend access to the Software for security, maintenance, non-payment, or suspected misuse without constituting termination.

9. INDEMNIFICATION
Customer agrees to indemnify the Company against claims arising from: Customer breaching this Agreement, Customer data or content, or Improper use of the Software. Company provides no indemnity to Customer.

10. GOVERNING LAW & DISPUTE RESOLUTION
10.1 Governing Law
This Agreement is governed by the laws of Sri Lanka, unless both parties agree otherwise in writing.

10.2 Dispute Resolution
Any dispute shall be resolved through: Good-faith negotiation (30 days) followed by Arbitration, conducted in English, under internationally recognized rules (UNCITRAL or ICC), seated in Colombo or an agreed neutral location.

11. GENERAL TERMS
The Agreement contains the entire understanding between parties. Updates may be made with notice. Invalid clauses do not affect the remainder. Notices may be delivered electronically.

11.1 Governing Documents and Policies
This Agreement incorporates by reference the Classveew Terms & Conditions, Privacy Policy, Support Policy, and any other policies or guidelines published by the Company from time to time (collectively, the "Policies"). In the event of any conflict between this Agreement and the Policies, this Agreement shall prevail, unless expressly stated otherwise.`,

  acknowledgmentText:
    'I have read and agree to this Classveew Master Subscription Agreement and all linked Terms of Service and Policies',
};

// ============================================================================
// ARABIC AGREEMENT CONTENT (selected by language; English remains the default)
// ============================================================================
export const AGREEMENT_CONTENT_AR: IAgreementContent = {
  title: 'تأكيد الوصول واتفاقية التجربة من CLASSVEEW',
  section1: `1. الوصول الأولي واستخدام المنصة
يُمنح العميل حق الوصول إلى برنامج Classveew لتقييم مدى ملاءمته للاستخدام الداخلي.

1.1. منح الوصول: تمنح الشركة العميل حقًا غير حصري وقابلًا للإلغاء للوصول إلى ميزات منصة Classveew الكاملة لفترة تجريبية مدتها ${TRIAL_PERIOD_DURATION === 'fourteen (14) days' ? 'أربعة عشر (14) يومًا' : TRIAL_PERIOD_DURATION} من تاريخ البدء.

1.2. انتهاء التجربة: سينتهي هذا الوصول تلقائيًا عند انقضاء الفترة التجريبية المذكورة أعلاه.

1.3. إغلاق الخدمة: تحتفظ الشركة بالحق في إنهاء هذا الوصول التجريبي في أي وقت إذا لزم الأمر، وستزوّد العميل بإشعار مسبق لا يقل عن أسبوع واحد (1) في مثل هذه الحالات.`,
  section2: `2. الإقرار القانوني والشروط الملزمة
يدرك العميل أنه بتوقيع هذا التأكيد، فإنه يوافق على الشروط القانونية الأساسية التالية التي تحكم استخدامه للمنصة.

2.1. الشروط الكاملة: يقر العميل ويوافق على أن شروط خدمة Classveew الكاملة وشروط الاستخدام وسياسة الخصوصية (المتاحة على www.classveew.com) مُدمجة بالإحالة وتنطبق فورًا على استخدامه.

2.2. الحمايات الأساسية المتفق عليها: بالتوقيع أدناه، يوافق العميل تحديدًا على المبادئ الحمائية التالية:
  • ملكية البرنامج: تحتفظ الشركة بجميع الحقوق والملكية والملكية الفكرية المتعلقة بالبرنامج والكود والمنصة.
  • مسؤولية بيانات العميل: العميل مسؤول عن دقة وأمن وقانونية جميع البيانات المرفوعة إلى المنصة، بما في ذلك تأمين الموافقات القانونية اللازمة من أولياء الأمور والموظفين (الامتثال لـ PDPA).
  • مسؤولية الشركة المحدودة: يوافق العميل على أن المسؤولية المالية للشركة عن أي مشكلات أو أضرار في الخدمة محدودة بأدنى مبلغ يسمح به القانون.
  • بوابة الدفع: تُسهّل المنصة المدفوعات عبر بوابة WebX Pay. العميل مسؤول عن حساب WebX Pay الخاص به وجميع رسوم المعاملات وعمليات رد المبالغ والامتثال المالي.
  • القانون الحاكم: تخضع هذه الاتفاقية وجميع الاستخدامات المتعلقة بالمنصة لقوانين ومحاكم جمهورية سريلانكا الديمقراطية الاشتراكية.`,
  section3: `3. استمرار الخدمة
3.1. الاشتراك للمتابعة: إذا رغب العميل في مواصلة استخدام منصة Classveew بعد الفترة التجريبية، فسيحتاج إلى الاشتراك في باقة مدفوعة.

3.2. الخطوة التالية: لشراء اشتراك (الباقة الأساسية أو القياسية أو المميزة أو المؤسسية)، يجب على العميل توقيع اتفاقية اشتراك CLASSVEEW منفصلة. سيتوقف الوصول فورًا عند انقضاء الفترة التجريبية البالغة ${TRIAL_PERIOD_DURATION_SHORT === '14 days' ? '14 يومًا' : TRIAL_PERIOD_DURATION_SHORT} إذا لم تكن هناك اتفاقية اشتراك سارية.

تأكيد العميل: يؤكد العميل أنه قرأ ووافق على شروط تأكيد الوصول هذا وشروط الخدمة الكاملة.`,
  acknowledgmentText:
    'لقد قرأت ووافقت على تأكيد وصول Classveew هذا وجميع شروط الخدمة المرتبطة',
};

export const SUBSCRIPTION_AGREEMENT_CONTENT_AR: IAgreementContent = {
  title: 'CLASSVEEW – اتفاقية الاشتراك الرئيسية',
  section1: `1. حقوق الوصول والترخيص
1.1 منح الترخيص
تمنح الشركة العميل ترخيصًا غير حصري وغير قابل للتحويل وقابلًا للإلغاء لاستخدام منصات Classveew على الويب والجوال للعمليات الأكاديمية والإدارية الداخلية.

1.2 المستخدمون المصرّح لهم
حسب مستوى الاشتراك، قد يشمل الوصول:
• الموظفون
• المعلمون
• الطلاب
• المستخدمون الإداريون
• أولياء الأمور (مباشرة أو عبر باقات يدفعها الوالدان)

1.3 باقات الاشتراك
تشمل الباقات على سبيل المثال لا الحصر: الأساسية والقياسية والمميزة والمؤسسية. الميزات المتاحة في كل باقة هي تلك التي تنشرها الشركة وقت الاستخدام، سواء على موقع الشركة الرسمي أو داخل البرنامج. قد تقدّم الشركة ميزات جديدة من وقت لآخر وقد تعدّل الميزات الحالية بشكل معقول، شريطة ألا تقلل هذه التغييرات جوهريًا من الوظائف الأساسية لاشتراك العميل النشط خلال فترة مدفوعة.`,
  section2: `2. الرسوم والفوترة والدفع
2.1 رسوم الاشتراك
تستند الرسوم إلى:
• تسعير سنوي لكل طالب
• مستوى الميزات
• وحدات إضافية مشتراة
• أي معايير أخرى متفق عليها

2.2 شروط الدفع
ما لم يُذكر خلاف ذلك:
• تُستحق الفواتير خلال سبعة (7) أيام
• قد يؤدي التأخر في الدفع إلى التعليق

2.3 الضرائب
جميع الرسوم لا تشمل الضرائب. العميل مسؤول عن الضرائب المطبقة.

2.4 مدفوعات اشتراك الشركة
تُدفع رسوم الاشتراك المستحقة من العميل للشركة مقابل استخدام البرنامج مباشرة إلى الشركة عبر الطرق التي تحددها الشركة، والتي قد تشمل التحويل البنكي أو معالجي الدفع الخارجيين المعتمدين. العميل مسؤول عن جميع رسوم المعاملات المطبقة المتعلقة بهذه المدفوعات.`,
  section3: `3. مستوى الخدمة والدعم والتوافر
3.1 الدعم
مستوى الدعم وفقًا لسياسة دعم Classveew.

3.2 توافر البرنامج
نبذل جهودًا معقولة تجاريًا للحفاظ على استمرارية الخدمة. يُقدَّم البرنامج "كما هو" و"حسب توافره" دون أي ضمان لاتفاقية مستوى الخدمة للتوافر.

4. الملكية الفكرية
4.1 الملكية
تحتفظ الشركة بجميع حقوق الملكية الفكرية العالمية لـ Classveew:
• البرنامج
• الكود المصدري
• تصاميم واجهة/تجربة المستخدم
• قواعد البيانات
• الميزات
• التوثيق

4.2 القيود
لا يجوز للعميل: النسخ أو الهندسة العكسية أو التعديل أو إعادة البيع أو التأطير أو إعادة التوزيع ما لم يُسمح بذلك كتابيًا.

5. مسؤوليات العميل
5.1 قانونية البيانات
العميل مسؤول مسؤولية كاملة عن الجمع والرفع القانوني لجميع البيانات الشخصية.

5.2 التزامات الموافقة
يؤكد العميل أنه حصل على الموافقات المطلوبة لـ: بيانات الطلاب، وبيانات أولياء الأمور، وبيانات الموظفين، والصور والفيديوهات والسجلات التعليمية، وأي بيانات ومعلومات أخرى. يشمل ذلك الامتثال لأي قوانين خصوصية مطبقة في بلد العميل (GDPR وCOPPA وPIPEDA وPDPA أو أطر محلية أخرى).

5.3 بوابات الدفع
عند استخدام بوابات دفع خارجية متكاملة (Stripe وWebX Pay وغيرها)، يقر العميل بأن البرنامج يوفر تكاملًا تقنيًا. العميل مسؤول وحده عن: (أ) إنشاء وصيانة حساب التاجر، و(ب) جميع رسوم المعاملات وعمليات رد المبالغ، و(ج) الالتزام بشروط مزود بوابة الدفع.

5.4 مسؤولية الموافقة على الوسائط
العميل مسؤول وحده عن ضمان أن أي صور أو فيديوهات أو وسائط للطلاب تُرفع إلى المنصة قد تم الحصول عليها واستخدامها بشكل قانوني وبموافقة صالحة من الوالدين أو الأوصياء. لا تتحقق الشركة من هذه الموافقة ولن تكون مسؤولة عن الاستخدام غير المصرّح أو غير السليم.

5.5 القوة القاهرة
لن يكون أي طرف مسؤولًا عن التأخيرات أو الإخفاقات الناجمة عن أحداث خارجة عن السيطرة المعقولة، بما في ذلك الكوارث الطبيعية أو انقطاعات الإنترنت أو الإجراءات الحكومية.

6. خصوصية البيانات والأمن
6.1 أدوار مراقب ومعالج البيانات
• العميل = مراقب البيانات
• الشركة = معالج البيانات

6.2 الأمن
تستخدم الشركة ضمانات إدارية ومادية وتقنية معقولة مصممة لحماية أمن وسرية وسلامة المنصة وبيانات العميل. تُستضاف المنصة على بنية Amazon Web Services (AWS) التحتية.

6.3 نقل البيانات
يقر العميل بأن البيانات قد تُخزَّن أو تُعالَج في مناطق سحابية عالمية.

6.4 تصدير وحذف البيانات
عند الإنهاء: لدى العميل 30 يومًا لتصدير البيانات. بعد 30 يومًا، قد تُحذف البيانات.

6.5 استخدام المعالجين الفرعيين
يقر العميل بأن الشركة قد تستعين بمعالجين فرعيين، بما في ذلك مزودي البنية التحتية السحابية وخدمات التحليلات ومزودي الاتصالات. تظل الشركة مسؤولة عن التزامات الامتثال لكنها لن تكون مسؤولة عن الإخفاقات الخارجة عن السيطرة المعقولة.

7. حدود المسؤولية
7.1 لا ضمان
يُقدَّم البرنامج دون أي ضمانات من أي نوع.

7.2 سقف المسؤولية
تقتصر المسؤولية الإجمالية على الأكبر من: 100 دولار أمريكي أو إجمالي الرسوم المدفوعة في الـ 12 شهرًا السابقة.

7.3 استبعاد الأضرار
لا مسؤولية عن: الأضرار غير المباشرة أو الأرباح الفائتة أو فقدان البيانات أو انقطاع العمل.

7.4 إخلاء المسؤولية عن النتائج
لا تضمن الشركة النتائج الأكاديمية أو أداء الطلاب أو نتائج الامتثال أو الموافقات التنظيمية الناشئة عن استخدام البرنامج.

8. المدة والإنهاء
8.1 المدة
تستمر هذه الاتفاقية حتى يتم إنهاؤها.

8.2 الإنهاء بإشعار
يجوز لأي طرف الإنهاء بإشعار كتابي مسبق مدته 30 يومًا.

8.3 الإنهاء الفوري
يجوز للشركة الإنهاء فورًا في حالات: عدم الدفع أو سوء الاستخدام أو الإخلال الجوهري.

8.4 ما بعد الإنهاء
يتوقف الوصول فورًا؛ وتُطبَّق قواعد تصدير البيانات.

8.5 تعليق الوصول
يجوز للشركة تعليق الوصول إلى البرنامج مؤقتًا لأسباب الأمن أو الصيانة أو عدم الدفع أو الاشتباه في سوء الاستخدام دون أن يُعد ذلك إنهاءً.

9. التعويض
يوافق العميل على تعويض الشركة عن المطالبات الناشئة عن: إخلال العميل بهذه الاتفاقية، أو بيانات أو محتوى العميل، أو الاستخدام غير السليم للبرنامج. لا تقدم الشركة أي تعويض للعميل.

10. القانون الحاكم وحل النزاعات
10.1 القانون الحاكم
تخضع هذه الاتفاقية لقوانين سريلانكا، ما لم يتفق الطرفان كتابيًا على خلاف ذلك.

10.2 حل النزاعات
يُحل أي نزاع من خلال: التفاوض بحسن نية (30 يومًا) يتبعه التحكيم، الذي يُجرى بالإنجليزية، وفق قواعد معترف بها دوليًا (UNCITRAL أو ICC)، ومقره كولومبو أو موقع محايد متفق عليه.

11. الأحكام العامة
تتضمن الاتفاقية كامل التفاهم بين الطرفين. يجوز إجراء تحديثات بإشعار. لا تؤثر البنود غير الصالحة على البقية. يجوز تسليم الإشعارات إلكترونيًا.

11.1 المستندات والسياسات الحاكمة
تتضمن هذه الاتفاقية بالإحالة شروط وأحكام Classveew وسياسة الخصوصية وسياسة الدعم وأي سياسات أو إرشادات أخرى تنشرها الشركة من وقت لآخر (يُشار إليها مجتمعةً بـ "السياسات"). في حال وجود أي تعارض بين هذه الاتفاقية والسياسات، تسود هذه الاتفاقية ما لم يُنص صراحةً على خلاف ذلك.`,
  acknowledgmentText:
    'لقد قرأت ووافقت على اتفاقية اشتراك Classveew الرئيسية هذه وجميع شروط الخدمة والسياسات المرتبطة',
};

export const AGREEMENT_VALIDATION_MESSAGES_AR = {
  organizationName: 'يجب أن يتراوح اسم المؤسسة بين 3 و255 حرفًا',
  organizationAddress: 'يجب أن يتراوح العنوان بين 5 و500 حرف',
  businessRegistrationNumber: 'يجب أن يتراوح رقم التسجيل التجاري بين 3 و50 حرفًا',
  primaryContactName: 'يجب أن يتراوح الاسم بين 2 و100 حرف',
  primaryContactTitle: 'يجب أن يتراوح المنصب بين 2 و100 حرف',
  acknowledgmentAccepted: 'يجب عليك قبول الاتفاقية للمتابعة',
  scrollRequired: 'يرجى التمرير لقراءة الاتفاقية كاملة قبل القبول',
};

/** Returns the trial agreement content for the given language code. */
export const getAgreementContent = (lang?: string): IAgreementContent =>
  lang && lang.startsWith('ar') ? AGREEMENT_CONTENT_AR : AGREEMENT_CONTENT;

/** Returns the master subscription agreement content for the given language code. */
export const getSubscriptionAgreementContent = (lang?: string): IAgreementContent =>
  lang && lang.startsWith('ar') ? SUBSCRIPTION_AGREEMENT_CONTENT_AR : SUBSCRIPTION_AGREEMENT_CONTENT;

/** Returns the agreement validation messages for the given language code. */
export const getAgreementValidationMessages = (lang?: string) =>
  lang && lang.startsWith('ar') ? AGREEMENT_VALIDATION_MESSAGES_AR : AGREEMENT_VALIDATION_MESSAGES;
